import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Typography,
    Box,
    Button,
    Stack,
    Divider,
    CircularProgress,
    Link,
    Chip,
} from '@mui/material';
import { API_URL } from '../../api';
import AccountStatusCard from '../../components/AccountStatusCard';
import AccountDangerZone from '../../components/AccountDangerZone';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const Field = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography color="text.secondary">{label}</Typography>
        <Typography>{value || '—'}</Typography>
    </Box>
);

export default function TeacherDetailPage() {
    const { id } = useParams();
    const [teacher, setTeacher] = useState(null);

    const load = async () => {
        const { data } = await axios.get(`${API_URL}/api/admin/accounts/${id}`, authHeaders());
        setTeacher(data);
    };

    useEffect(() => { load(); }, [id]);

    if (!teacher) {
        return (
            <Container>
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h4">
                    {teacher.firstName} {teacher.surname}
                </Typography>
                <Button component={RouterLink} to="/teacher-accounts">
                    Back
                </Button>
            </Stack>

            <AccountStatusCard
                account={teacher}
                onChanged={(next) => next && setTeacher(next)}
            />

            <AccountDangerZone account={teacher} />

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Contact</Typography>
                <Field label="Email" value={teacher.email} />
                <Field label="Email verified" value={teacher.emailVerifiedAt ? 'Yes' : 'No'} />
                <Field label="Phone" value={teacher.phone} />
                <Field label="Last login" value={teacher.lastLoginAt ? new Date(teacher.lastLoginAt).toLocaleString('en-GB') : '—'} />
                <Field label="Signed up" value={teacher.createdAt ? new Date(teacher.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} />
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Can teach</Typography>
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 1.5 }}>
                    Subjects + levels the tutor said they can teach when they signed up. Independent of the classes they've actually been assigned.
                </Typography>
                {teacher.teachingSubjects?.length ? (
                    <Stack spacing={1}>
                        {teacher.teachingSubjects.map((s, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 500 }}>{s.subject}</Typography>
                                {(s.levels || []).map((lvl) => (
                                    <Chip key={lvl} size="small" label={lvl} variant="outlined" />
                                ))}
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography color="text.secondary">Not stated.</Typography>
                )}
            </Card>

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Current classes</Typography>
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 1.5 }}>
                    Published classes this tutor is assigned to teach.
                </Typography>
                {teacher.currentClasses?.length ? (
                    <Stack spacing={0.5}>
                        {teacher.currentClasses.map((c) => (
                            <Link
                                key={c._id}
                                component={RouterLink}
                                to={`/tutoring-classes/${c._id}`}
                                underline="hover"
                            >
                                {c.name} · {c.studentCount} student{c.studentCount === 1 ? '' : 's'}
                            </Link>
                        ))}
                    </Stack>
                ) : (
                    <Typography color="text.secondary">Not currently teaching any published class.</Typography>
                )}
            </Card>

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
                Teacher ID: {teacher._id}
            </Typography>
        </Container>
    );
}
